import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ChangeDetectorRef,
  ViewChild
} from '@angular/core';
import { Router } from '@angular/router';
import { UtilService } from '@app-shared/services/util.service';
import { SupportChatService, SupportConversation, SupportChatMessage, SupportFaqItem } from '@app-shared/services/support-chat.service';
import { BotContext, BotHandoff, BotIdentity } from '@app-shared/services/support-bot.service';
import { SocketService } from '@app/socket.service';
import { Subscription } from 'rxjs';

export interface SupportOption {
  id: string;
  title: string;
  iconType: 'chat' | 'question' | 'faq' | 'users';
  route?: string;
  action?: () => void;
  badge?: string;
  permissionKey?: string;
  chatTitle?: string;
}

export interface ChatMessage {
  id: string;
  sender: 'incoming' | 'outgoing' | 'system';
  text: string;
  author_name?: string;
  created_at?: string;
  time?: string;
}

@Component({
  selector: 'app-support-widget',
  templateUrl: './support-widget.component.html',
  styleUrls: ['./support-widget.component.scss'],
  standalone: false
})
export class SupportWidgetComponent implements OnInit, OnDestroy {
  @Input() position: 'right' = 'right';
  @ViewChild('chatMessagesContainer') chatMessagesContainer?: ElementRef;

  isOpen = false;
  currentView: 'menu' | 'chat' | 'user-list' | 'faq' | 'bot' = 'menu';
  botContext: BotContext | null = null;
  previousView: 'menu' | 'user-list' = 'menu';

  activeChatTitle: string = 'Support Team';
  activeChatSubtitle: string = '';
  newMessageText: string = '';

  messages: ChatMessage[] = [];
  conversations: SupportConversation[] = [];
  selectedConversation: SupportConversation | null = null;
  hasUnreadAdminMessage: boolean = false;

  showUserInfoModal: boolean = false;
  activeUserInfo: any = null;

  isLoadingMessages: boolean = false;
  isLoadingConversations: boolean = false;
  isLoadingFaqs: boolean = false;

  isAutoRefreshEnabled: boolean = true;
  private autoRefreshTimer: any = null;

  // FAQ state loaded from PostgreSQL DB
  faqs: SupportFaqItem[] = [];
  faqSearchQuery: string = '';
  activeFaqCategory: string = 'All';
  activeFaqIndex: number | null = 0;

  // Guest user info state
  guestEmail: string = '';
  guestPhone: string = '';
  showGuestInfoPrompt: boolean = false;
  pendingGuestMessage: string = '';
  guestFormErrors: { email?: string; phone?: string } = {};

  private subs: Subscription = new Subscription();

  supportOptions: SupportOption[] = [
    {
      id: 'chat-with-us',
      title: 'Chat with Us',
      iconType: 'chat',
      chatTitle: 'Support Team',
      permissionKey: 'ChatWithUs'
    },
    {
      id: 'faq',
      title: 'FAQ',
      iconType: 'faq',
    },
    {
      id: 'chat-with-users',
      title: 'Chat with Users',
      iconType: 'users',
      chatTitle: 'Users Support'
    },
  ];

  constructor(
    private router: Router,
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef,
    private util: UtilService,
    private supportChatService: SupportChatService,
    private socketService: SocketService
  ) {}

  ngOnInit(): void {
    try {
      this.guestEmail = localStorage.getItem('support_guest_email') || '';
      this.guestPhone = localStorage.getItem('support_guest_phone') || '';

      // Check for unread admin messages on initial page load for logged in users
      if (this.isLoggedIn && !this.isAdmin) {
        this.checkUnreadAdminMessages();
      }

      // Check Chat with Users access for current user email and active organization
      this.checkChatWithUsersAccess();

      this.subs.add(
        this.socketService.onNewNotification().subscribe((data: any) => {
          if (data && (data.title?.includes('Support') || data.message?.includes('support') || data.message?.includes('Support') || data.service_type === 'IN APP PUSH')) {
            // If logged-in user receives an admin reply and chat is not actively open, turn Support button RED
             if (this.isLoggedIn && !this.isAdmin && (!this.isOpen || (this.currentView !== 'chat' && this.currentView !== 'bot'))) {
               this.hasUnreadAdminMessage = true;
               this.cdr.detectChanges();
             }

            if (this.isOpen) {
              if (this.currentView === 'chat') {
                const targetUserId = this.selectedConversation ? this.selectedConversation.user_id : this.currentUserId;
                const targetOrgId = this.selectedConversation ? this.selectedConversation.org_id : this.currentOrgId;
                const isAdminViewing = !!this.selectedConversation;
                this.silentRefreshMessages(targetUserId, targetOrgId, isAdminViewing);
              }
            }
          }
        })
      );
    } catch (_) {}
  }

  ngOnDestroy(): void {
    this.stopAutoRefresh();
    this.subs.unsubscribe();
  }

  private anonymousSessionUserId: number = 0;

  get isLoggedIn(): boolean {
    const token = localStorage.getItem('JwtToken') || localStorage.getItem('token');
    const uid = localStorage.getItem('UserId') || localStorage.getItem('userId') || localStorage.getItem('id');
    return !!(token && uid);
  }

  get currentUserId(): number {
    if (!this.isLoggedIn) {
      const storedGuestId = localStorage.getItem('support_guest_user_id');
      if (storedGuestId && parseInt(storedGuestId, 10)) {
        this.anonymousSessionUserId = parseInt(storedGuestId, 10);
        return this.anonymousSessionUserId;
      }
      if (!this.anonymousSessionUserId) {
        this.anonymousSessionUserId = 9000000 + Math.floor(Math.random() * 900000);
        try {
          localStorage.setItem('support_guest_user_id', String(this.anonymousSessionUserId));
        } catch (_) {}
      }
      return this.anonymousSessionUserId;
    }
    const raw = localStorage.getItem('UserId') || localStorage.getItem('userId') || localStorage.getItem('id');
    return raw ? parseInt(raw, 10) : 1;
  }

  formatNameFromEmail(email: string): string {
    if (!email || typeof email !== 'string') return '';
    const cleanEmail = email.trim();
    const usernamePart = cleanEmail.split('@')[0];
    if (!usernamePart) return cleanEmail;
    const words = usernamePart.split(/[._-]+/).filter(w => w.length > 0);
    if (words.length > 0) {
      return words.map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
    }
    return usernamePart;
  }

  get currentUserName(): string {
    if (!this.isLoggedIn) {
      const guestName = localStorage.getItem('support_guest_name');
      if (guestName && guestName.trim() && guestName !== 'null' && guestName !== 'undefined') {
        return guestName.trim();
      }
      const guestEmail = localStorage.getItem('support_guest_email') || this.guestEmail;
      if (guestEmail && guestEmail.trim()) {
        return this.formatNameFromEmail(guestEmail) || guestEmail.trim();
      }
      return 'Anonymous User';
    }
    const candidateKeys = ['username', 'fullname', 'full_name', 'name', 'user_name', 'useremail', 'email'];
    for (const key of candidateKeys) {
      const val = localStorage.getItem(key);
      if (val && typeof val === 'string') {
        const trimmed = val.trim();
        if (trimmed && trimmed !== 'null' && trimmed !== 'undefined') {
          const lower = trimmed.toLowerCase();
          if (lower !== 'user' && lower !== 'anonymous' && lower !== 'anonymous user' && !/^user\s*\d*$/i.test(lower)) {
            return trimmed;
          }
        }
      }
    }
    const fallback = localStorage.getItem('username') || localStorage.getItem('fullname') || localStorage.getItem('full_name') || localStorage.getItem('useremail') || '';
    if (fallback && typeof fallback === 'string' && fallback.trim() && fallback !== 'null' && fallback !== 'undefined') {
      return fallback.trim();
    }
    const uid = localStorage.getItem('UserId') || localStorage.getItem('userId') || localStorage.getItem('id');
    return uid ? `User ${uid}` : 'User';
  }

  get currentUserEmail(): string {
    if (!this.isLoggedIn) {
      return localStorage.getItem('support_guest_email') || this.guestEmail || '';
    }
    return localStorage.getItem('useremail') || localStorage.getItem('email') || '';
  }

  get currentUserPhone(): string {
    if (!this.isLoggedIn) {
      return localStorage.getItem('support_guest_phone') || this.guestPhone || '';
    }
    return localStorage.getItem('phone') || localStorage.getItem('userphone') || '';
  }

  get currentOrgId(): number {
    if (!this.isLoggedIn) {
      return 0;
    }
    const rawEnc = localStorage.getItem('OrgId');
    if (rawEnc) {
      const dec = this.util.decrypt(rawEnc);
      const parsed = parseInt(dec, 10);
      if (!isNaN(parsed)) return parsed;
    }
    const plainOrg = localStorage.getItem('org_id');
    if (plainOrg) {
      const parsed = parseInt(plainOrg, 10);
      if (!isNaN(parsed)) return parsed;
    }
    return 1;
  }

  get currentOrgName(): string {
    if (!this.isLoggedIn) {
      return 'Guest / Visitor';
    }
    const storedName = localStorage.getItem('current_org_name');
    if (storedName && storedName.trim()) return storedName;

    try {
      const encList = localStorage.getItem('userOrgList');
      if (encList) {
        let orgs: any[] = [];
        if (this.util.isJson(encList)) {
          orgs = JSON.parse(encList);
        } else {
          const dec = this.util.decrypt(encList);
          if (dec && this.util.isJson(dec)) {
            orgs = JSON.parse(dec);
          }
        }
        if (Array.isArray(orgs) && orgs.length > 0) {
          const match = orgs.find(o => Number(o.org_id) === this.currentOrgId) || orgs[0];
          return match?.org_name || match?.name || match?.organization_name || 'Certiplate';
        }
      }
    } catch (e) {
      // ignore
    }
    return 'Certiplate';
  }

  /** Get user role abbreviation from local storage */
  private getUserRole(): string {
    if (!this.isLoggedIn) {
      return 'Guest';
    }
    const encRole = localStorage.getItem('ur');
    return encRole ? (this.util.decrypt(encRole) || '').toUpperCase() : '';
  }

  get isSystemSuperAdmin(): boolean {
    if (!this.isLoggedIn) return false;
    const isSsa = localStorage.getItem('isSystemSuperAdmin') === 'true';
    const role = (localStorage.getItem('role') || '').toLowerCase();
    const ur = this.getUserRole();
    const orgId = this.currentOrgId;
    const email = (localStorage.getItem('useremail') || '').toLowerCase();
    return isSsa || ur === 'SSA' || role === 'system super admin' || (ur === 'SA' && orgId === -1) || email === 'admin@navriti.com';
  }

  get isSuperAdmin(): boolean {
    const role = (localStorage.getItem('role') || '').toLowerCase();
    const email = (localStorage.getItem('useremail') || '').toLowerCase();
    const ur = this.getUserRole();
    return this.isSystemSuperAdmin || ur === 'SA' || role === 'super admin' || email === 'admin@navriti.com';
  }

  get isOrgAdmin(): boolean {
    const role = (localStorage.getItem('role') || '').toLowerCase();
    const ur = this.getUserRole();
    const isOrgSuper = localStorage.getItem('isOrgSuperAdmin') === 'true';
    return ur === 'OA' || role === 'organization admin' || isOrgSuper;
  }

  get isAdmin(): boolean {
    return this.isSystemSuperAdmin || this.isSuperAdmin || this.isOrgAdmin || this.hasChatWithUsersAccess;
  }

  /**
   * Check feature permissions from decrypted menu_access / sys_access
   */
  private checkPermission(permissionKeys: string[]): boolean {
    try {
      const encSysAccess = localStorage.getItem('sys_access');
      if (encSysAccess) {
        let sysAccess: any = null;
        if (this.util.isJson(encSysAccess)) {
          sysAccess = JSON.parse(encSysAccess);
        } else {
          const decryptedSysAccess = this.util.decrypt(encSysAccess);
          if (decryptedSysAccess) {
            sysAccess = typeof decryptedSysAccess === 'string' && this.util.isJson(decryptedSysAccess)
              ? JSON.parse(decryptedSysAccess)
              : decryptedSysAccess;
          }
        }
        if (Array.isArray(sysAccess)) {
          const hasSysMatch = sysAccess.some((item: any) => {
            const name = (typeof item === 'string' ? item : item?.name || item?.permission || '').toLowerCase();
            return permissionKeys.some(k => name.includes(k.toLowerCase()));
          });
          if (hasSysMatch) return true;
        }
      }

      const encMenuAccess = localStorage.getItem('menu_access');
      if (encMenuAccess) {
        let menuAccess: any = null;
        if (this.util.isJson(encMenuAccess)) {
          menuAccess = JSON.parse(encMenuAccess);
        } else {
          const decryptedMenu = this.util.decrypt(encMenuAccess);
          if (decryptedMenu) {
            menuAccess = typeof decryptedMenu === 'string' && this.util.isJson(decryptedMenu)
              ? JSON.parse(decryptedMenu)
              : decryptedMenu;
          }
        }
        if (Array.isArray(menuAccess)) {
          const extractFeatures = (items: any[]): string[] => {
            const res: string[] = [];
            items.forEach(it => {
              if (it.features && Array.isArray(it.features)) {
                it.features.forEach((f: any) => res.push((f.feature_name || f.name || '').toLowerCase()));
              }
              if (it.pages && Array.isArray(it.pages)) {
                res.push(...extractFeatures(it.pages));
              }
            });
            return res;
          };

          const allFeatureNames = extractFeatures(menuAccess);
          const hasMenuMatch = permissionKeys.some(k => allFeatureNames.includes(k.toLowerCase()));
          if (hasMenuMatch) return true;
        }
      }

      return false;
    } catch (e) {
      return false;
    }
  }

  /** Permission gate for "Chat with Us" (Removed for SSA, available by default for non-SSA users) */
  get hasChatWithUsAccess(): boolean {
    // For System Super Admin (SSA), remove "Chat with Us"
    if (this.isSystemSuperAdmin) {
      return false;
    }
    return true;
  }

  isChatWithUsersAuthorized: boolean = false;

  /** Check Chat with Users permission based on email invitation and organization membership */
  checkChatWithUsersAccess(): void {
    if (!this.isLoggedIn) {
      this.isChatWithUsersAuthorized = false;
      return;
    }
    if (this.isSystemSuperAdmin) {
      this.isChatWithUsersAuthorized = true;
      return;
    }
    const email = this.currentUserEmail;
    const orgId = this.currentOrgId;
    const userId = this.currentUserId;
    if (!email) {
      this.isChatWithUsersAuthorized = false;
      return;
    }

    this.supportChatService.checkAccess(email, orgId, userId).subscribe({
      next: (res) => {
        this.isChatWithUsersAuthorized = !!(res && res.hasAccess);
        this.cdr.detectChanges();
      },
      error: () => {
        this.isChatWithUsersAuthorized = false;
        this.cdr.detectChanges();
      }
    });
  }

  /** Permission gate for "Chat with Users" (only visible if authorized for current organization) */
  get hasChatWithUsersAccess(): boolean {
    if (this.isSystemSuperAdmin) {
      return true;
    }
    if (!this.isLoggedIn) {
      return false;
    }
    return this.isChatWithUsersAuthorized;
  }

  /** Visible options based on user permissions */
  get visibleSupportOptions(): SupportOption[] {
    return this.supportOptions.filter(option => {
      if (option.id === 'chat-with-us') {
        return this.hasChatWithUsAccess;
      }
      if (option.id === 'chat-with-users') {
        return this.hasChatWithUsersAccess;
      }
      return true;
    });
  }

  markMessagesAsRead(): void {
    if (this.isLoggedIn) {
      this.hasUnreadAdminMessage = false;
      try {
        localStorage.setItem(`support_chat_last_read_${this.currentUserId}`, new Date().toISOString());
      } catch (_) {}
      this.cdr.detectChanges();
    }
  }

  checkUnreadAdminMessages(): void {
    if (!this.isLoggedIn || this.isAdmin) return;
    const clearedKey = this.getClearedAtKey(this.currentUserId, this.currentOrgId);
    const clearedAt = localStorage.getItem(clearedKey);
    const clearedTimestamp = clearedAt ? new Date(clearedAt).getTime() : 0;

    this.supportChatService.getMessages(this.currentUserId, this.currentOrgId).subscribe({
      next: (msgs: SupportChatMessage[]) => {
        if (Array.isArray(msgs) && msgs.length > 0) {
          const isMsgAdmin = (m: SupportChatMessage) => {
            const st = (m.sender_type || '').toLowerCase();
            const auth = (m.author_name || '').toLowerCase();
            return st === 'admin' || st === 'admin_msg' || st === 'sa' || st === 'ssa' ||
              auth.includes('admin') || auth === 'support team' || auth === 'system super admin';
          };
          const lastMsg = msgs[msgs.length - 1];
          if (isMsgAdmin(lastMsg)) {
            const msgTime = lastMsg.created_at ? new Date(lastMsg.created_at).getTime() : 0;
            if (!clearedTimestamp || msgTime > clearedTimestamp) {
              const lastReadAt = localStorage.getItem(`support_chat_last_read_${this.currentUserId}`);
              const lastReadTime = lastReadAt ? new Date(lastReadAt).getTime() : 0;
              if (!lastReadTime || msgTime > lastReadTime) {
                this.hasUnreadAdminMessage = true;
                this.cdr.detectChanges();
              }
            }
          }
        }
      },
      error: () => {}
    });
  }

  toggleWidget(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.isOpen = !this.isOpen;
    this.showUserInfoModal = false;
    if (!this.isOpen) {
      this.currentView = 'menu';
      this.selectedConversation = null;
      this.stopAutoRefresh();
    } else {
      this.markMessagesAsRead();
      this.startAutoRefresh();
    }
    this.cdr.detectChanges();
  }

  openWidget(): void {
    if (!this.isOpen) {
      this.isOpen = true;
      this.showUserInfoModal = false;
      this.checkChatWithUsersAccess();
      this.markMessagesAsRead();
      this.startAutoRefresh();
      this.cdr.detectChanges();
    }
  }

  closeWidget(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (this.isOpen) {
      this.isOpen = false;
      this.showUserInfoModal = false;
      this.currentView = 'menu';
      this.selectedConversation = null;
      this.stopAutoRefresh();
      this.cdr.detectChanges();
    }
  }

  startAutoRefresh(): void {
    this.stopAutoRefresh();
    if (!this.isAutoRefreshEnabled) return;

    this.autoRefreshTimer = setInterval(() => {
      if (!this.isOpen) return;

      if (this.currentView === 'chat') {
        const targetUserId = this.selectedConversation ? this.selectedConversation.user_id : this.currentUserId;
        const targetOrgId = this.selectedConversation ? this.selectedConversation.org_id : this.currentOrgId;
        const isAdminViewing = !!this.selectedConversation;
        this.silentRefreshMessages(targetUserId, targetOrgId, isAdminViewing);
      }
    }, 3500);
  }

  stopAutoRefresh(): void {
    if (this.autoRefreshTimer) {
      clearInterval(this.autoRefreshTimer);
      this.autoRefreshTimer = null;
    }
  }

  toggleAutoRefresh(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.isAutoRefreshEnabled = !this.isAutoRefreshEnabled;
    if (this.isAutoRefreshEnabled) {
      this.startAutoRefresh();
    } else {
      this.stopAutoRefresh();
    }
    this.cdr.detectChanges();
  }

  silentRefreshMessages(userId: number, orgId: number, isAdminViewing: boolean): void {
    if (this.isLoadingMessages) return;

    const clearedKey = this.getClearedAtKey(userId, orgId);
    const clearedAt = !isAdminViewing ? localStorage.getItem(clearedKey) : null;
    const clearedTimestamp = clearedAt ? new Date(clearedAt).getTime() : 0;

    this.supportChatService.getMessages(userId, orgId).subscribe({
      next: (data: SupportChatMessage[]) => {
        if (data && Array.isArray(data)) {
          const rawMessages = data.filter(m => {
            if (isAdminViewing || !clearedTimestamp) return true;
            return m.created_at ? new Date(m.created_at).getTime() > clearedTimestamp : true;
          });

          const isMsgAdmin = (m: SupportChatMessage) => {
            const st = (m.sender_type || '').toLowerCase();
            const auth = (m.author_name || '').toLowerCase();
            return st === 'admin' || st === 'admin_msg' || st === 'sa' || st === 'ssa' ||
              auth.includes('admin') || auth === 'support team' || auth === 'system super admin';
          };

          const formatted: ChatMessage[] = rawMessages.map(m => {
            const fromAdmin = isMsgAdmin(m);
            return {
              id: m.id,
              text: m.text,
              author_name: m.author_name,
              created_at: m.created_at,
              sender: (isAdminViewing
                ? (fromAdmin ? 'outgoing' : 'incoming')
                : (fromAdmin ? 'incoming' : 'outgoing')) as 'incoming' | 'outgoing',
              time: m.time || ''
            };
          });

          // If server returns empty [] but client already has active messages (e.g., guest user), never wipe out messages
          if (formatted.length === 0 && this.messages.length > 0 && !isAdminViewing) {
            return;
          }

          const countChanged = formatted.length !== this.messages.length;
          const lastChanged = formatted.length > 0 && this.messages.length > 0 &&
            (formatted[formatted.length - 1]?.text !== this.messages[this.messages.length - 1]?.text ||
             formatted[formatted.length - 1]?.id !== this.messages[this.messages.length - 1]?.id);

          if (countChanged || lastChanged) {
            this.messages = formatted;
            const cacheKey = this.getCacheKey(userId, orgId);
            try {
              localStorage.setItem(cacheKey, JSON.stringify(this.messages));
            } catch (_) {}
            this.scrollToBottom();
            this.cdr.detectChanges();
          }
        }
      },
      error: () => {}
    });
  }

  silentRefreshConversations(): void {
    if (this.isLoadingConversations) return;

    this.supportChatService.getConversations().subscribe({
      next: (conversations) => {
        if (Array.isArray(conversations)) {
          const seen = new Set<number>();
          const filtered = conversations.filter(c => {
            const uId = Number(c?.user_id);
            if (!uId || seen.has(uId)) return false;
            seen.add(uId);
            return true;
          }).map(c => {
            let cleanName = c.user_name || '';
            const lower = cleanName.trim().toLowerCase();
            if (!cleanName || lower === 'user' || lower === 'null' || lower === 'undefined' || lower === 'anonymous' || lower === 'anonymous user') {
              if (c.user_email && c.user_email.trim() && c.user_email !== '—') {
                cleanName = this.formatNameFromEmail(c.user_email.trim());
              } else if (c.user_id && Number(c.user_id) < 9000000) {
                cleanName = `User ${c.user_id}`;
              } else {
                cleanName = 'Anonymous User';
              }
            }
            return {
              ...c,
              user_name: cleanName
            };
          });
          this.conversations = filtered;
          this.cdr.detectChanges();
        }
      },
      error: () => {}
    });
  }

  get filteredFaqs(): SupportFaqItem[] {
    let list = this.faqs || [];
    const query = this.faqSearchQuery ? this.faqSearchQuery.trim().toLowerCase() : '';
    if (query) {
      list = list.filter(f =>
        (f.question && f.question.toLowerCase().includes(query)) ||
        (f.answer && f.answer.toLowerCase().includes(query)) ||
        (f.category && f.category.toLowerCase().includes(query))
      );
    }
    return list;
  }

  loadFaqs(): void {
    this.isLoadingFaqs = true;
    this.supportChatService.getFaqs().subscribe({
      next: (data) => {
        this.faqs = (data || []).filter(f => f.is_active !== false);
        this.isLoadingFaqs = false;
        if (this.faqs.length > 0 && this.activeFaqIndex === null) {
          this.activeFaqIndex = 0;
        }
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.warn('Failed to fetch FAQs:', err);
        this.isLoadingFaqs = false;
        this.cdr.detectChanges();
      }
    });
  }

  toggleFaqItem(index: number, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.activeFaqIndex = this.activeFaqIndex === index ? null : index;
    this.cdr.detectChanges();
  }

  clearFaqSearch(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.faqSearchQuery = '';
    this.cdr.detectChanges();
  }

  switchToChatFromFaq(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    const chatOption = this.supportOptions.find(o => o.id === 'chat-with-us');
    if (chatOption && this.hasChatWithUsAccess) {
      this.onSelectOption(chatOption, event);
    } else {
      this.currentView = 'menu';
      this.cdr.detectChanges();
    }
  }

  backAction(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.showUserInfoModal = false;
    this.currentView = 'menu';
    this.selectedConversation = null;
    this.startAutoRefresh();
    this.cdr.detectChanges();
  }

  onSelectOption(option: SupportOption, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.showUserInfoModal = false;

    if (option.id === 'chat-with-us') {
      this.markMessagesAsRead();
      this.previousView = 'menu';
      this.selectedConversation = null;
      //this.activeChatTitle = 'Support Team';
      //this.activeChatSubtitle = '';
      //this.currentView = 'chat';
      //this.loadMessages(this.currentUserId, this.currentOrgId, false);
      //this.startAutoRefresh();
      this.botContext = {
        user_id: this.currentUserId,
        organization_id: this.currentOrgId,
        user_name: this.currentUserName,
        email: this.currentUserEmail,
        phone: this.currentUserPhone,
        role: this.getUserRole(),
        org_name: this.currentOrgName,
      };
      this.currentView = 'bot';
      this.cdr.detectChanges();
      return;
    }

    if (option.id === 'faq') {
      this.previousView = 'menu';
      this.currentView = 'faq';
      this.faqSearchQuery = '';
      this.loadFaqs();
      this.cdr.detectChanges();
      return;
    }

    if (option.id === 'chat-with-users') {
      this.closeWidget();
      this.router.navigate(['/chat-with-users']);
      return;
    }

    this.closeWidget();

    if (option.action) {
      option.action();
    } else if (option.route) {
      this.router.navigate([option.route]);
    }
  }

  loadConversations(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.isLoadingConversations = true;
    this.cdr.detectChanges();

    this.subs.add(
      this.supportChatService.getConversations().subscribe({
        next: (conversations) => {
          if (Array.isArray(conversations)) {
            const seen = new Set<number>();
            this.conversations = conversations.filter(c => {
              const uId = Number(c?.user_id);
              if (!uId || seen.has(uId)) {
                return false;
              }
              seen.add(uId);
              return true;
            }).map(c => {
              let cleanName = c.user_name || '';
              const lower = cleanName.trim().toLowerCase();
              if (!cleanName || lower === 'user' || lower === 'null' || lower === 'undefined' || lower === 'anonymous' || lower === 'anonymous user') {
                if (c.user_email && c.user_email.trim() && c.user_email !== '—') {
                  cleanName = this.formatNameFromEmail(c.user_email.trim());
                } else if (c.user_id && Number(c.user_id) < 9000000) {
                  cleanName = `User ${c.user_id}`;
                } else {
                  cleanName = 'Anonymous User';
                }
              }
              return {
                ...c,
                user_name: cleanName
              };
            });
          } else {
            this.conversations = [];
          }
          this.isLoadingConversations = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Error in loadConversations:', err);
          this.isLoadingConversations = false;
          this.cdr.detectChanges();
        }
      })
    );
  }

  /**
   * Clear chat on the user side only.
   * Preserves full history in Cassandra and on SSA side.
   */
  clearUserChat(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    const clearedTime = new Date().toISOString();
    const clearedKey = this.getClearedAtKey(this.currentUserId, this.currentOrgId);
    const cacheKey = this.getCacheKey(this.currentUserId, this.currentOrgId);

    try {
      localStorage.setItem(clearedKey, clearedTime);
      localStorage.removeItem(cacheKey);
    } catch (e) {
      // ignore
    }

    this.messages = [];
    this.newMessageText = '';
    this.isLoadingMessages = false;
    this.cdr.detectChanges();
  }

  toggleUserInfoModal(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (this.selectedConversation) {
      this.activeUserInfo = {
        user_id: this.selectedConversation.user_id,
        user_name: this.selectedConversation.user_name || 'User',
        user_email: this.selectedConversation.user_email || '—',
        phone: this.selectedConversation.phone || this.selectedConversation.user_phone || '—',
        org_id: this.selectedConversation.org_id || 0,
        org_name: this.selectedConversation.org_name || 'Certiplate',
        role: this.selectedConversation.role || 'User'
      };
      this.showUserInfoModal = !this.showUserInfoModal;
    } else if (this.currentView === 'chat') {
      this.activeUserInfo = {
        user_id: this.currentUserId,
        user_name: this.currentUserName,
        user_email: this.currentUserEmail || '—',
        phone: this.currentUserPhone || '—',
        org_id: this.currentOrgId,
        org_name: this.currentOrgName,
        role: this.getUserRole() || 'User'
      };
      this.showUserInfoModal = !this.showUserInfoModal;
    }
    this.cdr.detectChanges();
  }

  closeUserInfoModal(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.showUserInfoModal = false;
    this.cdr.detectChanges();
  }

  onSelectConversation(conv: SupportConversation, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.selectedConversation = conv;
    this.showUserInfoModal = false;
    conv.has_unread = false;
    this.previousView = 'user-list';
    this.activeChatTitle = conv.user_name;
    this.activeChatSubtitle = (conv.user_email && conv.user_email !== '—') 
      ? conv.user_email 
      : conv.org_name;
    this.currentView = 'chat';
    this.loadMessages(conv.user_id, conv.org_id, true);
    this.cdr.detectChanges();
  }

  private getCacheKey(userId: number, orgId: number): string {
    return `support_chat_${userId}_${orgId || 0}`;
  }

  private getClearedAtKey(userId: number, orgId: number): string {
    return `support_chat_cleared_at_${userId}_${orgId || 0}`;
  }

  loadMessages(userId: number, orgId: number, isAdminViewing: boolean): void {
    const isAnonymous = !this.isLoggedIn && !isAdminViewing;
    const cacheKey = this.getCacheKey(userId, orgId);
    const clearedKey = this.getClearedAtKey(userId, orgId);
    const clearedAt = !isAdminViewing ? localStorage.getItem(clearedKey) : null;
    const clearedTimestamp = clearedAt ? new Date(clearedAt).getTime() : 0;

    // Load from cache for all users (including logged-out/guest users)
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length > 0) {
          this.messages = parsed.filter((m: any) => {
            if (isAdminViewing || !clearedTimestamp) return true;
            return m.created_at ? new Date(m.created_at).getTime() > clearedTimestamp : true;
          });
          this.scrollToBottom();
          this.cdr.detectChanges();
        }
      } catch (e) {
        // ignore
      }
    }

    this.isLoadingMessages = true;
    this.cdr.detectChanges();

    this.subs.add(
      this.supportChatService.getMessages(userId, orgId).subscribe({
        next: (data: SupportChatMessage[]) => {
          if (data && Array.isArray(data)) {
            const rawMessages = data.filter(m => {
              if (isAdminViewing || !clearedTimestamp) return true;
              return m.created_at ? new Date(m.created_at).getTime() > clearedTimestamp : true;
            });

            const isMsgAdmin = (m: SupportChatMessage) => {
              const st = (m.sender_type || '').toLowerCase();
              const auth = (m.author_name || '').toLowerCase();
              return st === 'admin' || st === 'admin_msg' || st === 'sa' || st === 'ssa' ||
                auth.includes('admin') || auth === 'support team' || auth === 'system super admin';
            };

            const serverMessages = rawMessages.map(m => {
              const fromAdmin = isMsgAdmin(m);
              return {
                id: m.id,
                text: m.text,
                author_name: m.author_name,
                created_at: m.created_at,
                sender: (isAdminViewing
                  ? (fromAdmin ? 'outgoing' : 'incoming')
                  : (fromAdmin ? 'incoming' : 'outgoing')) as 'incoming' | 'outgoing',
                time: m.time || ''
              };
            });

            // Update messages if server returned data, or if we had no cached messages.
            // If server returns empty [] but client has cached messages for guest user, do not wipe.
            if (serverMessages.length > 0 || this.messages.length === 0 || isAdminViewing) {
              this.messages = serverMessages;
              try {
                localStorage.setItem(cacheKey, JSON.stringify(this.messages));
              } catch (e) {
                // ignore
              }
            }
          }
          this.isLoadingMessages = false;
          this.scrollToBottom();
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Error in loadMessages:', err);
          this.isLoadingMessages = false;
          this.cdr.detectChanges();
        }
      })
    );
  }

  submitGuestInfo(event?: Event): void {
    if (event) {
      event.preventDefault();
    }
    this.guestFormErrors = {};
    const email = (this.guestEmail || '').trim();
    const phone = (this.guestPhone || '').trim();

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      this.guestFormErrors.email = 'Email address is required.';
    } else if (!emailRegex.test(email)) {
      this.guestFormErrors.email = 'Please enter a valid email address.';
    }

    if (!phone) {
      this.guestFormErrors.phone = 'Phone number is required.';
    } else if (phone.replace(/[^\d]/g, '').length < 7) {
      this.guestFormErrors.phone = 'Please enter a valid phone number.';
    }

    if (this.guestFormErrors.email || this.guestFormErrors.phone) {
      this.cdr.detectChanges();
      return;
    }

    const derivedName = this.formatNameFromEmail(email);
    try {
      localStorage.setItem('support_guest_email', email);
      localStorage.setItem('support_guest_phone', phone);
      localStorage.setItem('support_guest_name', derivedName);
    } catch (_) {}

    this.showGuestInfoPrompt = false;

    // If there was a pending message waiting to be sent, send it now
    const msgToSend = this.pendingGuestMessage || this.newMessageText;
    if (msgToSend && msgToSend.trim()) {
      this.newMessageText = msgToSend;
      this.pendingGuestMessage = '';
      this.sendMessage();
    } else {
      this.cdr.detectChanges();
    }
  }

  cancelGuestPrompt(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.showGuestInfoPrompt = false;
    this.pendingGuestMessage = '';
    this.cdr.detectChanges();
  }

  sendMessage(event?: Event): void {
    if (event) {
      event.preventDefault();
    }
    const text = (this.newMessageText || '').trim();
    if (!text) return;

    const isAdminViewing = !!this.selectedConversation;

    // For logged-out users, if email is not yet provided, prompt for email & phone before sending
    if (!this.isLoggedIn && !isAdminViewing) {
      const savedEmail = localStorage.getItem('support_guest_email');
      if (!savedEmail || !savedEmail.trim()) {
        this.pendingGuestMessage = text;
        this.guestEmail = this.guestEmail || '';
        this.guestPhone = this.guestPhone || '';
        this.guestFormErrors = {};
        this.showGuestInfoPrompt = true;
        this.cdr.detectChanges();
        return;
      }
    }

    const now = new Date();
    const timeStr = now.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    const nowIso = now.toISOString();

    const targetUserId = isAdminViewing
      ? this.selectedConversation!.user_id
      : this.currentUserId;

    const targetOrgId = isAdminViewing
      ? this.selectedConversation!.org_id
      : this.currentOrgId;

    const senderName = this.currentUserName || (isAdminViewing ? 'Admin' : 'User');
    const senderRole = this.getUserRole() || (isAdminViewing ? 'Admin' : 'OU');
    const senderOrg = this.currentOrgName || 'Certiplate';

    // If user is sending a new message after clearing, reset the cleared timestamp
    if (!isAdminViewing) {
      try {
        localStorage.removeItem(this.getClearedAtKey(targetUserId, targetOrgId));
      } catch (_) {}
    }

    // Optimistically add message to UI
    const tempMsg: ChatMessage = {
      id: String(Date.now()),
      sender: 'outgoing',
      author_name: senderName,
      created_at: nowIso,
      text: text,
      time: timeStr
    };
    this.messages.push(tempMsg);
    this.newMessageText = '';

    // Cache immediately in localStorage
    const cacheKey = this.getCacheKey(targetUserId, targetOrgId);
    try {
      localStorage.setItem(cacheKey, JSON.stringify(this.messages));
    } catch (e) {
      // ignore
    }

    this.scrollToBottom();
    this.cdr.detectChanges();

    // Send to Cassandra & backend API
    this.supportChatService.sendMessage({
      user_id: targetUserId,
      organization_id: targetOrgId,
      user_name: senderName,
      user_email: !isAdminViewing ? this.currentUserEmail : undefined,
      phone: !isAdminViewing ? this.currentUserPhone : undefined,
      role: senderRole,
      org_name: senderOrg,
      message_text: text,
      is_admin: isAdminViewing
    }).subscribe({
      next: (res) => {
        if (res && res.id) {
          tempMsg.id = String(res.id);
          try {
            localStorage.setItem(cacheKey, JSON.stringify(this.messages));
          } catch (e) {
            // ignore
          }
          this.cdr.detectChanges();
        }
      },
      error: (err) => {
        console.error('Failed to save message to server:', err);
      }
    });
  }

   /** Bot -> "Talk to human": switch to the existing live chat; the transcript is already in the thread. */
  onBotHandoff(_handoff?: BotHandoff): void {
   this.markMessagesAsRead();
   this.previousView = 'menu';
   this.selectedConversation = null;
   this.activeChatTitle = 'Support Team';
   this.activeChatSubtitle = '';
   this.currentView = 'chat';
   this.loadMessages(this.currentUserId, this.currentOrgId, false);
  this.startAutoRefresh();
   this.cdr.detectChanges();
  }

 /** Guest details collected inside the bot are stored under the same keys the guest prompt uses. */
 onBotIdentity(identity: BotIdentity): void {
    if (this.isLoggedIn || !identity) return;
   try {
    if (identity.email) {
        localStorage.setItem('support_guest_email', identity.email);
        this.guestEmail = identity.email;
      }
      if (identity.phone) {
        localStorage.setItem('support_guest_phone', identity.phone);
        this.guestPhone = identity.phone;
      }
      if (identity.name && identity.email) {
        localStorage.setItem('support_guest_name', identity.name);
      }
    } catch (_) {}
  }



  private scrollToBottom(): void {
    setTimeout(() => {
      if (this.chatMessagesContainer?.nativeElement) {
        const container = this.chatMessagesContainer.nativeElement;
        container.scrollTop = container.scrollHeight;
      }
    }, 50);
  }

  trackByConv(index: number, item: SupportConversation): number {
    return item.user_id;
  }

  trackByMsg(index: number, item: ChatMessage): string {
    return item.id || String(index);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen) return;

    const clickedInside = this.elementRef.nativeElement.contains(event.target);
    if (!clickedInside) {
      this.closeWidget();
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(): void {
    if (this.isOpen) {
      this.closeWidget();
    }
  }
}
